import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { useCallback, useEffect, useRef } from 'react'
import { create } from 'zustand'

interface UseGlobalFileUploadResult {
  isActive: boolean
  showUploadArea: () => void
  hideUploadArea: () => void
  onUploadComplete: (documentReferences: ExtendedDocumentReference[]) => void
}

interface UseGlobalFileUploadOptions {
  onUploadComplete?: (documentReferences: ExtendedDocumentReference[]) => void
}

interface GlobalFileUploadStore {
  isActive: boolean
  showUploadArea: () => void
  hideUploadArea: () => void
}

const useGlobalFileUploadStore = create<GlobalFileUploadStore>((set) => ({
  isActive: false,
  showUploadArea: () => set({ isActive: true }),
  hideUploadArea: () => set({ isActive: false }),
}))

export function useGlobalFileUpload({
  onUploadComplete: onUploadCompleteProp,
}: UseGlobalFileUploadOptions = {}): UseGlobalFileUploadResult {
  const isActive = useGlobalFileUploadStore((s) => s.isActive)
  const showUploadArea = useGlobalFileUploadStore((s) => s.showUploadArea)
  const hideUploadArea = useGlobalFileUploadStore((s) => s.hideUploadArea)

  const onUploadCompleteRef = useRef<
    ((documentReferences: ExtendedDocumentReference[]) => void) | undefined
  >(onUploadCompleteProp)
  useEffect(() => {
    onUploadCompleteRef.current = onUploadCompleteProp
  }, [onUploadCompleteProp])

  const onUploadComplete = useCallback(
    (documentReferences: ExtendedDocumentReference[]) => {
      if (onUploadCompleteRef.current) {
        onUploadCompleteRef.current(documentReferences)
      }
      hideUploadArea()
    },
    [hideUploadArea]
  )

  return {
    isActive,
    showUploadArea,
    hideUploadArea,
    onUploadComplete,
  }
}
